/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'dashboard-analytics/widgets/livewidget/nls/StringResources',
	'dashboard-analytics/visualizations/vipr/VIPRUtils',
	'dashboard-analytics/visualizations/vipr/VIPRConfig'
], function(_, StringResources, VIPRUtils, VIPRConfig) {
	'use strict';


	/*
	* The puspose of this singleton is to provide a central point to create generic
	* Color properties in a format consumeable by the properties panel. Currently,
	* only Vida based visualizations use this object. There will be future work if
	* we want to make this even more generic to allow other vis types to use it.
	}
	*/
	const ColorPropertiesCreator = (() => {

		/**
		 * Instance stores a reference to the Singleton
		 * @type {object}
		 */
		let instance = null;

		function init() {
			return {
				/*
				========================================================================
				Property creation section
				========================================================================
				*/

				/**
				* @returns Create the color type property in a format consumeable by the
				* properties panel
				* Note, we currently do not have any public properties of the type color
				* so we will be setting all these properties to have public === false for
				* now. We also only have color themeable properties so we must check if
				* theme-ing should apply here.
				*/
				createColorProperty: function(options) {
					let propDesc = {
						id: options.name,
						type: 'Color',
						propertyType: 'Color',
						label: options.caption,
						'ariaLabel': options.caption,
						public: false,
						'sectionName': StringResources.get('sectionName_colorPalette'),
						'tabName': StringResources.get('tabName_visualization'),
						name: options.name,
						displayPos: 10, // This is TBD but we want color properties to lead the list for now.
						active: options.active ? options.active : false
					};
					return propDesc;
				},

				/**
				* @returns a categorical palette property description
				*/
				getCategoricalColorPalettePropertyDesc: function() {
					return {
						'id': 'colorPalette',
						'name': 'colorPalette',
						'type': 'NewPalette',
						'sectionLabel': StringResources.get('propColorPalette'),
						'newPaletteLabel': StringResources.get('propNewColorPaletteLabel'),
						'linkLabel': StringResources.get('propColorPaletteChangeLabel'),
						'enableReorder': false,
						'ariaLabel': StringResources.get('propColorPalette'),
						'sectionName': StringResources.get('sectionName_colorPalette'),
						'tabName': StringResources.get('tabName_visualization'),
						'public': true,
						'paletteType': 'ColorPalette',
						'displayPos': 0,
						'reversePalettePropName': 'heatScalePalette',
						active: true
					};
				},

				/**
				* @param palette - vipr property object
				* @param possibleDataSlots - all possible slots for the current vis type
				* @returns the data layer Id if one exists, otherwise undefined.
				*/
				getDataLayerIdFromPaletteAndSlots: function(palette, possibleDataSlots) {
					var slotName = palette && palette.slot && palette.slot.name;
					var dataSlot = slotName && _.find(possibleDataSlots, slot => slotName === slot.getId());
					var layerId = dataSlot && dataSlot.getDefinition().getDatasetIdList()[0];
					// We only want to return a valid layer id if we support multi layers
					// All single layers return layerId of 'data', so check for this case.
					// [254850] Radial chart returns layerId of 'dataset', we need to check this case.
					return layerId === 'data' || layerId === 'dataset' ? null : layerId;
				},

				/**
				* @param dataLayerId - data layer for the prop, may be undefined or null
				* @returns the continuous color palette property name for the specified layer
				*/
				getContinuousColorPalettePropertyNameWithLayer: function(dataLayerId) {
					return dataLayerId ? 'contColorPalette_' + dataLayerId: 'contColorPalette';
				},

				/**
				* @param dataLayerId - data layer for the prop, may be undefined or null
				* @returns the categorical color palette property name for the specified layer
				*/
				_getCategoricalColorPalettePropertyNameWithLayer: function(dataLayerId) {
					return dataLayerId ? 'colorPalette_' + dataLayerId: 'colorPalette';
				},

				/**
				* @param dataLayerId - data layer for the prop, may be undefined or null
				* @returns the heat scale palette property name for the specified layer
				*/
				getHeatScalePalettePropertyNameWithLayer: function(dataLayerId) {
					return dataLayerId ? 'heatScalePalette_' + dataLayerId: 'heatScalePalette';
				},

				/**
				* @param layerDesc - layer description for the prop, may be undefined or null
				* @returns the heat scale palette property section name for the specified layer
				*/
				getHeatScalePalettePropertySectionNameWithLayer: function(layerDesc) {
					// Default to the color palette section.
					return StringResources.get(layerDesc && layerDesc.colorRelatedProps.sectionNameId || 'sectionName_colorPalette');
				},

				/**
				* @param configuration - bundle configuration for the visualization of interest
				* includes overrides provided by livewidget
				* @param layerId - layer id of interest
				* @returns description of the layer in the configuration
				*	{
				*		id: 'data.region',
				*		colorRelatedProps {
				*			transparencyProp: 'region.transparency',
				*			defaultPaletteValue: 'colorPalette0'
				*		}
				*	}
				*/
				_getLayerDescriptionUsingId: function(configuration, layerId) {
					return _.find(configuration.layerDescriptions, function(desc) {return desc.id === layerId;});
				},

				/**
				* @param configuration - bundle configuration for the visualization of interest
				* includes overrides provided by livewidget
				* @param layerId - layer id of interest
				* @returns a categorical palette property description
				*/
				_getCategoricalColorPalettePropertyDescWithLayerId: function(configuration, dataLayerId) {
					var id = instance._getCategoricalColorPalettePropertyNameWithLayer(dataLayerId);
					var heatScaleId = instance.getHeatScalePalettePropertyNameWithLayer(dataLayerId);
					var layerDesc = instance._getLayerDescriptionUsingId(configuration, dataLayerId);

					const propertyDesc = [{
						'id': id,
						'name': id,
						'type': 'NewPalette',
						'sectionLabel': StringResources.get(layerDesc && layerDesc.colorRelatedProps.labelId || 'propColorPalette'),
						'newPaletteLabel': StringResources.get('propNewColorPaletteLabel'),
						'linkLabel': StringResources.get('propColorPaletteChangeLabel'),
						'enableReorder': false,
						'ariaLabel': StringResources.get(layerDesc && layerDesc.colorRelatedProps.labelId || 'propColorPalette'),
						'sectionName': instance.getHeatScalePalettePropertySectionNameWithLayer(layerDesc),
						'tabName': StringResources.get('tabName_visualization'),
						'public': true,
						'paletteType': 'ColorPalette',
						'displayPos': 0,
						'reversePalettePropName': heatScaleId,
						defaultValue: layerDesc ? layerDesc.colorRelatedProps.defaultPaletteValue : null,
						active: true
					}];
					if(layerDesc && _.has(layerDesc.colorRelatedProps, 'defaultColorIndex')) {
						propertyDesc.push({
							'id': id + '_defaultIndex',
							'displayPos': 1,
							'type': 'ColorPicker',
							'public': true,
							'showHexValue': true,
							'sectionName': StringResources.get('sectionName_colorPalette'),
							'tabName': StringResources.get('tabName_visualization'),
							'palettePropertyName': id,
							'paletteType': 'ColorPalette',
							'defaultValue': layerDesc.colorRelatedProps.defaultColorIndex.defaultValue || 0,
							'label': StringResources.get(layerDesc.colorRelatedProps.defaultColorIndex.labelId),
							'ariaLabel': StringResources.get(layerDesc.colorRelatedProps.defaultColorIndex.labelId),
							'active': true,
							'addMissingBaseColors': false
						});
					}
					return propertyDesc;
				},

				/**
				* @param configuration - bundle configuration for the visualization of interest
				* includes overrides provided by livewidget
				* @param layerId - layer id of interest
				* @returns a continuous palette property description
				*/
				_getContinuousColorPalettePropertyDescWithLayerId: function(configuration, dataLayerId) {
					var id = instance.getContinuousColorPalettePropertyNameWithLayer(dataLayerId);
					var heatScaleId = instance.getHeatScalePalettePropertyNameWithLayer(dataLayerId);
					var layerDesc = instance._getLayerDescriptionUsingId(configuration, dataLayerId);
					return {
						'id': id,
						'name': id,
						'type': 'NewPalette',
						'sectionLabel': StringResources.get(layerDesc && (layerDesc.colorRelatedProps.contLabelId || layerDesc.colorRelatedProps.labelId) || 'propHeatScalePalette'),
						'newPaletteLabel': StringResources.get('propNewHeatPaletteLabel'),
						'linkLabel': StringResources.get('propHeatPaletteChangeLabel'),
						'open': false,
						'ariaLabel': StringResources.get(layerDesc && (layerDesc.colorRelatedProps.contLabelId || layerDesc.colorRelatedProps.labelId) || 'propHeatScalePalette'),
						'enableReorder': true,
						'paletteType': 'HeatPalette',
						'sectionName': instance.getHeatScalePalettePropertySectionNameWithLayer(layerDesc),
						'tabName': StringResources.get('tabName_visualization'),
						'reversePalettePropName': heatScaleId,
						defaultValue: layerDesc ? layerDesc.colorRelatedProps.defaultPaletteValue : null,
						active: true
					};
				},

				/**
				* @param layerId - layer id of interest
				* @param configuration - bundle configuration for the visualization of interest
				* @returns the heat scale order property description
				*/
				_getHeatScaleOrderPropertyDescriptionWithLayerId: function(configuration, dataLayerId) {
					const layerDesc = configuration && instance._getLayerDescriptionUsingId(configuration, dataLayerId);
					return {
						'id': instance.getHeatScalePalettePropertyNameWithLayer(dataLayerId),
						'public': false,
						'active': true,
						'sectionName': instance.getHeatScalePalettePropertySectionNameWithLayer(layerDesc),
						'tabName': StringResources.get('tabName_visualization')
					};
				},

				/**
				* @returns the heat scale order property description
				*/
				_getHeatScaleOrderPropertyDescription: function() {
					return instance._getHeatScaleOrderPropertyDescriptionWithLayerId(null /*no layer id*/);
				},


				/**
				* @param configuration - bundle configuration for the visualization of interest
				* includes overrides provided by livewidget
				* @returns an array of property description items for all layers
				*/
				_getColorPalettePropertiesForLayersFromVisId: function(configuration) {
					var items  = [];
					configuration.layerDescriptions.forEach((layerDesc) => {
						/*
						* Currently each property can have a continuous palette, a transparency
						* and a heat scale property. This may need to change as more
						* properties per layer are introduced.
						*/
						items.push.apply(items, instance._getCategoricalColorPalettePropertyDescWithLayerId(configuration, layerDesc.id));
						items.push(instance._getContinuousColorPalettePropertyDescWithLayerId(configuration, layerDesc.id));
						var transparencyPropertyName = layerDesc.colorRelatedProps.transparencyProp;
						var propDesc = {
							name: transparencyPropertyName,
							active: true
						};
						let item = instance.getTransparencyPropertyForLayer(propDesc);
						if (item) {
							items.push(item);
						}
						var heatScaleItem = instance._getHeatScaleOrderPropertyDescriptionWithLayerId(configuration, layerDesc.id);
						if (heatScaleItem) {
							items.push(heatScaleItem);
						}
					});
					return items;
				},

				/**
				* @param paletteType - type of palette (cat, cont, single)
				* @param configuration - bundle configuration for the visualization of interest
				* includes overrides provided by livewidget
				* @param sectionItems - array of section property items to add the new properties to
				* @param layerId - layer id of interest
				* @returns array of color palette property descriptions
				*/
				_getColorPalettePropertyForLayer: function(paletteType, configuration, sectionItems, layerId) {
					// @todo do we need to support 'customcat' and 'customcont' palettes for layers
					switch ( paletteType ) {
						case 'cat':
							sectionItems.push.apply(sectionItems, instance._getCategoricalColorPalettePropertyDescWithLayerId(configuration, layerId));
							sectionItems.push(instance._getHeatScaleOrderPropertyDescriptionWithLayerId(configuration, layerId));
							break;
						case 'cont':
							sectionItems.push(instance._getContinuousColorPalettePropertyDescWithLayerId(configuration, layerId));
							sectionItems.push(instance._getHeatScaleOrderPropertyDescriptionWithLayerId(configuration, layerId));
							break;
						case 'single':
							sectionItems.push(instance._getCategoricalColorPalettePropertyDesc());
							break;
					}
				},

				/**
				* @param propertyDesc - vipr transparency property of interest
				* @returns transparency property description
				*/
				getTransparencyPropertyForLayer: function(options) {
					if (options) {
						return {
							'name': options.name,
							'id': options.name,
							'label': options.caption,
							'ariaLabel': options.caption,
							'type': null,
							'module': 'lib/@waca/core-client/js/core-client/ui/UiSlider',
							'sliderType': 'percentage',
							'description': StringResources.get('propTransparencyDescription'),
							'connect': [true, false],
							'defaultValue': options.value || options.defaultValue ? options.value || options.defaultValue : 0.20,
							'range': {
								'min': 0,
								'max': 100
							},
							'step': 1,
							'format': {
								'decimals': 1,
								'postfix': '%'
							},
							'displayPos': 19,
							'noInputView': true,
							'sectionName': StringResources.get('sectionName_colorPalette'),
							'tabName': StringResources.get('tabName_visualization'),
							active: options.active ? options.active : false
						};
					}
					return null;
				},

				/**
				* @param viprProperties
				* @param configuration - bundle configuration for the visualization of interest
				* includes overrides provided by livewidget
				* @param palette - vipr palette
				* @param possibleDataSlots - all possible slots for the current vis type
				* @param sectionItems - array of section property items to add the new properties to
				*/
				_getColorPaletteLayerTransparencyPropertyForLayer: function(viprProperties, configuration, palette, possibleDataSlots, sectionItems) {
					var dataLayerId = instance.getDataLayerIdFromPaletteAndSlots(palette, possibleDataSlots);
					var layerDesc = _.find(configuration.layerDescriptions, function(desc) {return desc.id === dataLayerId;});
					var viprProperty = viprProperties.get(layerDesc.colorRelatedProps.transparencyProp);

					var item = instance.getTransparencyPropertyForLayer(viprProperty);
					if (item) {
						sectionItems.push(item);
					}
				},

				//TODO: Move out.
				/**
				* @param viprProperties
				* @param possibleDataSlots - all possible slots for the current vis type
				* @returns an array of all the palette properties relevant to the current context in a format consumeable by the prop panel
				*/
				_getColorPaletteProperties: function(viprProperties, possibleDataSlots, visId) {
					var items  = [];

					// Only create property descrtiptions for the palettes pertinent to the current context (viz)
					// Also only include heat scale/continuous if needed.
					var configuration = VIPRConfig.getConfig(visId);
					if (configuration && configuration.supportsLayers) {
						VIPRUtils.getPalettes(viprProperties, visId).forEach(function(_palette) {
							var layerId = instance.getDataLayerIdFromPaletteAndSlots(_palette, possibleDataSlots);
							if (layerId) {
								instance._getColorPalettePropertyForLayer(_palette.paletteType, configuration, items, layerId);
								instance._getColorPaletteLayerTransparencyPropertyForLayer(viprProperties, configuration, _palette, possibleDataSlots, items);
							}
						}.bind(instance));

						// Create the section and then each item
					} else {
						if (configuration) {
							// Only create property descrtiptions for the palettes pertinent to the current context (viz)
							VIPRUtils.getPalettes(viprProperties, visId).forEach( _palette => {
								switch ( _palette.paletteType ) {
									case 'cat':
									case 'customcat':
										items.push(instance._getCategoricalColorPalettePropertyDesc());
										items.push(instance._getHeatScaleOrderPropertyDescription());
										break;
									case 'cont':
									case 'customcont':
										items.push(instance._getContinuousColorPalettePropertyDescWithLayerId(configuration));
										items.push(instance._getHeatScaleOrderPropertyDescription());
										break;
								}
							});
						}
					}
					return items;
				},

				/**
				* @returns true iff there is a palette of the type specified.
				*/
				_includesPalettesOfType: function(palettes, typeToMatch) {
					return palettes.some(palette => {
						return palette && palette.paletteType === typeToMatch;
					});
				},

				/**
				* @returns a default palette index property in a format that is consumeable by the property panel
				*/
				getDefaultPaletteIndexProperty: function() {
					return {
						'id': 'defaultPaletteIndex',
						'displayPos': 1,
						'type': 'ColorPicker',
						'public': true,
						'showHexValue': true,
						'sectionName': StringResources.get('sectionName_colorPalette'),
						'tabName': StringResources.get('tabName_visualization'),
						'palettePropertyName': 'colorPalette',
						'paletteType': 'ColorPalette',
						'defaultColorIndex': 0,
						'label': StringResources.get('propElementColor'),
						'ariaLabel': StringResources.get('propElementColor'),
						'active': true,
						'addMissingBaseColors': false
					};
				},

				/**
				* @param {Object} propInfo - single palette prop descriptions
				*{
				*	id: 'defaultPaletteIndex',
				*	labelId: 'propColumnColor',
				*	defaultColorIndex: 0
				*}
				* @returns a single palette color property in a format that is consumeable by the property panel
				*/
				getSinglePaletteColorProperty: function(propInfo) {
					return {
						'id': propInfo.id,
						'displayPos': 1,
						'type': 'ColorPicker',
						'public': true,
						'showHexValue': true,
						'sectionName': StringResources.get('sectionName_colorPalette'),
						'tabName': StringResources.get('tabName_visualization'),
						'palettePropertyName': 'colorPalette',
						'paletteType': 'ColorPalette',
						'defaultColorIndex': propInfo.defaultColorIndex,
						'label': StringResources.get(propInfo.labelId),
						'ariaLabel': StringResources.get(propInfo.labelId),
						'active': true,
						'addMissingBaseColors': false
					};
				},

				/**
				* @param possibleDataSlots
				* @returns true iff the color slot is encoded
				*/
				_isColorSlotEncoded: function(possibleDataSlots = []) {
					const colorSlot = possibleDataSlots.find(slot => slot.getId() === 'color');
					return !!(colorSlot && colorSlot.getDataItemList().length);
				},

				/**
				* @returns a categorical palette property description
				*/
				_getCategoricalColorPalettePropertyDesc: function() {
					return {
						'id': 'colorPalette',
						'name': 'colorPalette',
						'type': 'NewPalette',
						'sectionLabel': StringResources.get('propColorPalette'),
						'newPaletteLabel': StringResources.get('propNewColorPaletteLabel'),
						'linkLabel': StringResources.get('propColorPaletteChangeLabel'),
						'enableReorder': false,
						'ariaLabel': StringResources.get('propColorPalette'),
						'sectionName': StringResources.get('sectionName_colorPalette'),
						'tabName': StringResources.get('tabName_visualization'),
						'public': true,
						'paletteType': 'ColorPalette',
						'displayPos': 0,
						'reversePalettePropName': 'heatScalePalette',
						active: true
					};
				},

				_addCategoricalPalletePropForSinglePalletesIfNeeded(mappedItems, viprProperties, visId) {
					// If there isn't a previously created categorical property (needed to
					// provide color choices), create one now.
					const includesCat = instance._includesPalettesOfType(VIPRUtils.getPalettes(viprProperties, visId), 'cat');
					const configuration = VIPRConfig.getConfig(visId);
					if (!includesCat && !configuration.doesNotSupportCategoricalPalette) {
						let catProp = instance._getCategoricalColorPalettePropertyDesc();
						mappedItems[catProp.id] = catProp;
					}
				},

				/**
				* Add the color properties that VIPR doesn't call out directly (color palette)
				* or that need special attention (defualtPaletteIndex)
				* @param mappedItems - map of propert id to properties in a form consumeable
				* by the properties panel
				* @param viprProperties
				* @param possibleDataSlots - all possible slots for the current vis type
				* @param {string} visId - visualization type that generates the color properties
				* @param {object} content - the content that generates the color properties
				* @param {object} dashboard - dashboard api that allows get Colors service
				* @param {boolean} isPropertiesV2Enabled - feature flag to turn on PropertiesV2
				*/
				addColorRelatedProperties: function(mappedItems, viprProperties, possibleDataSlots, visId, content, dashboard, isPropertiesV2Enabled) {
					// If the color slot is not filled then we should add the element and
					// possibly line color
					var colorPaletteProperties = instance._getColorPaletteProperties(viprProperties, possibleDataSlots, visId);
					colorPaletteProperties.forEach(function(prop) {
						mappedItems[prop.id] = prop;
					});

					const colorProperties = [];

					// If there are single palettes, create the color properties needed
					if (VIPRUtils.getSinglePalettes(viprProperties, visId).length > 0) {
						// If there isn't a previously created categorical property (needed to
						// provide color choices), create one now.
						instance._addCategoricalPalletePropForSinglePalletesIfNeeded(mappedItems, viprProperties, visId);
						// Get the single color properties from the config and create them.
						let singlePaletteProps = VIPRUtils.getSinglePaletteProperties(visId) || {};
						Object.values(singlePaletteProps).forEach(singlePalettePropDesc => {
							const prop = instance.getSinglePaletteColorProperty(singlePalettePropDesc);
							mappedItems[prop.id] = prop;
							colorProperties.push(prop.id);
						});
					} else if (!instance._isColorSlotEncoded(possibleDataSlots) &&
						!VIPRUtils.doesConfigPropertyMatchExpected(visId, 'ignoreElementColor', true)) {
						// TODO: Add VIPRUtil calls as args. Also, add _isColorSlotEncoded as an arg
						/**
						 *  Dashboard property used for the element color when the color by slot is not used.
						 *  The property is also used for the column color in a composite chart
						 *
						 */
						var elementColorProp = instance.getDefaultPaletteIndexProperty();
						mappedItems[elementColorProp.id] = elementColorProp;
						colorProperties.push(elementColorProp.id);
					}
					if (isPropertiesV2Enabled) {
						const colorsService = dashboard.getFeature('Colors');
						colorProperties.forEach(propId => {
							const propDesc = mappedItems[propId];
							propDesc.paletteTitle = StringResources.get('colorPaletteTitle');
							propDesc.paletteId = propDesc.palettePropertyName ? instance._getColorPalettePropertyValue(content, colorsService, propDesc.palettePropertyName, propDesc.paletteType) : colorsService.getColorSetId();
						});

					}
				},

				/**
				* Checks to see if the palette property is set in the model, if it isn't will return the default palette name for the type
				* of palette being asked for
				* @param {object} content - the content which wants to display these properties
				* @param {object} colorsService - the Colors service feature
				* @param {string} palettePropertyName - the name of the property which contains the palette name
				* @param {string} paletteType - the type of palette being asked for
				* @returns palette name
				*/
				_getColorPalettePropertyValue(content, colorsService, palettePropertyName, paletteType) {
					const propertyValue = content.getPropertyValue(palettePropertyName);

					return propertyValue || colorsService.getDefaultPaletteName(paletteType);
				},

				/**
				* @param visId - visualization id of interest
				* @returns array of color palette property descriptions
				*/
				getColorPalettePropertiesFromVisId: function(visId) {
					// This means we are building the properties for the bundle provider,
					// return all possible properties.
					var configuration = VIPRConfig.getConfig(visId);
					// If the bundle supports layers then we need to create color props for each layer
					if (configuration && configuration.supportsLayers) {
						return instance._getColorPalettePropertiesForLayersFromVisId(configuration);
					} else {
						var items  = [];

						if (configuration) {
							// No layers support, create the basic props
							items.push(instance.getCategoricalColorPalettePropertyDesc(configuration));
							items.push(instance._getContinuousColorPalettePropertyDescWithLayerId(configuration));
							items.push(instance._getHeatScaleOrderPropertyDescription());
						}

						return items;
					}
				}

			};
		}

		return {

			// Get the Singleton instance if one exists
			// or create one if it doesn't
			getInstance: function () {

				if (!instance) {
					instance = init();
				}

				return instance;
			}
		};

	})();
	return ColorPropertiesCreator;
});
